import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useRequests } from '../../requests/useRequests'
import { RefreshActions } from '../../state/refresh'
import ItemsList from './ProjectDetails/ItemsList'
import { Project, ProjectItemComment, ProjectItem } from '../../types/project'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'
import { AttachmentsSection } from './ProjectDetails/components/AttachmentsSection'
import { State } from '../../types/state'
import { PROJECT_CATEGORIES } from './ProjectDetails/components/ItemModal'

interface IColumnType {
  [key: string]: boolean
  number: boolean
  stage: boolean
  name: boolean
  type: boolean
  price: boolean
  quantity: boolean
  unit: boolean
  value: boolean
  url: boolean
  image: boolean
  dimensions: boolean
  description: boolean
  status: boolean
  deliveryCost: boolean
  discountPercentage: boolean
  marginPercentage: boolean
  vatPercentage: boolean
  category: boolean
  catalogNumber: boolean
  supplier: boolean
  visualization: boolean
  attachment: boolean
}

const NEUTRAL_STATUS = 'pending' as ProjectItem['status']

const SharedProjectView = () => {
  const { shareId } = useParams<{ shareId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const { username } = useSelector((state: State) => state.user)

  const [project, setProject] = useState<Project | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedColumns, setSelectedColumns] = useState<IColumnType>({
    number: true,
    stage: true,
    name: true,
    type: true,
    price: true,
    quantity: true,
    unit: true,
    value: true,
    url: true,
    image: true,
    dimensions: true,
    description: true,
    status: true,
    deliveryCost: true,
    discountPercentage: true,
    marginPercentage: true,
    vatPercentage: true,
    category: true,
    catalogNumber: true,
    supplier: true,
    visualization: true,
    attachment: true
  })
  const [stageAttachments, setStageAttachments] = useState<{ [stageId: string]: any | null }>({})
  const [viewMode, setViewMode] = useState<'table' | 'client'>('client')
  const [comments, setComments] = useState<{ [itemId: string]: ProjectItemComment[] }>({})
  const [commentInputs, setCommentInputs] = useState<{ [itemId: string]: string }>({})
  const [isSending, setIsSending] = useState<{ [itemId: string]: boolean }>({})
  const [showVisualizationModal, setShowVisualizationModal] = useState(false)
  const [selectedVisualization, setSelectedVisualization] = useState<string | null>(null)

  const primaryButtonClasses = 'tw-bg-green-600 hover:tw-bg-green-700 focus:tw-bg-green-700 tw-text-white tw-font-semibold tw-py-2 tw-px-4 tw-rounded-md tw-shadow-sm tw-transition-colors tw-duration-150'
  const outlinePrimaryButtonClasses = 'tw-bg-transparent hover:tw-bg-green-50 tw-text-green-600 tw-font-semibold tw-py-2 tw-px-4 tw-border tw-border-solid tw-border-green-500 hover:tw-border-green-600 tw-rounded-md tw-transition-colors tw-duration-150'
  const smallButtonClasses = 'tw-py-1 tw-px-3 tw-text-xs'

  useEffect(() => {
    fetchSharedProject()
  }, [shareId])

  const fetchSharedProject = async () => {
    if (!shareId) return
    setIsLoading(true)
    try {
      const response = await requestMaker.getSharedProject(shareId)
      setProject({
        ...response.data,
        items: response.data.items
          .filter((item: any) => !item.isHidden)
      })
      if (response.data.selectedColumns) {
        setSelectedColumns(response.data.selectedColumns)
      }
    } catch (error) {
      console.error('Błąd podczas pobierania udostępnionego projektu', error)
      dispatch(RefreshActions.setToastMessage({ message: 'Nie udało się pobrać projektu - nieprawidłowy link lub projekt nie jest już dostępny' }))
      navigate('/')
    } finally {
      setIsLoading(false)
    }
  }

  const handleApproveItem = async (itemId: string): Promise<void> => {
    if (project == null) return
    const currentItem = project.items.find(item => item.id === itemId)
    if (currentItem == null) return

    const newStatus = currentItem.status === 'approved' ? NEUTRAL_STATUS : ('approved' as ProjectItem['status'])

    try {
      await requestMaker.updateProjectItemStatus(project.id, itemId, newStatus)
      setProject(prevProject => {
        if (prevProject == null) return null
        return {
          ...prevProject,
          items: prevProject.items.map(item =>
            item.id === itemId
              ? { ...item, status: newStatus }
              : item
          )
        }
      })
      const message = newStatus === 'approved'
        ? 'Element został zaakceptowany'
        : 'Akceptacja elementu została cofnięta.'
      dispatch(RefreshActions.setToastMessage({ message }))
    } catch (error) {
      console.error('Błąd podczas aktualizacji statusu elementu (approve/unapprove)', error)
      dispatch(RefreshActions.setToastMessage({ message: 'Nie udało się zaktualizować statusu elementu' }))
    }
  }

  const handleRejectItem = async (itemId: string): Promise<void> => {
    if (project == null) return
    const currentItem = project.items.find(item => item.id === itemId)
    if (currentItem == null) return

    const newStatus = currentItem.status === 'rejected' ? NEUTRAL_STATUS : ('rejected' as ProjectItem['status'])

    try {
      await requestMaker.updateProjectItemStatus(project.id, itemId, newStatus)
      setProject(prevProject => {
        if (prevProject == null) return null
        return {
          ...prevProject,
          items: prevProject.items.map(item =>
            item.id === itemId
              ? { ...item, status: newStatus }
              : item
          )
        }
      })
      const message = newStatus === 'rejected'
        ? 'Element został odrzucony'
        : 'Odrzucenie elementu zostało cofnięte.'
      dispatch(RefreshActions.setToastMessage({ message }))
    } catch (error) {
      console.error('Błąd podczas aktualizacji statusu elementu (reject/unreject)', error)
      dispatch(RefreshActions.setToastMessage({ message: 'Nie udało się zaktualizować statusu elementu' }))
    }
  }

  const handleSendMessage = (userId?: string) => navigate(userId ? `/messages?recipient=${userId}` : '/messages')

  const fetchComments = async (itemId: string) => {
    if (!project?.id) return
    try {
      const response = await requestMaker.getProjectItemComments(project.id, itemId)
      setComments(prev => ({ ...prev, [itemId]: response.data }))
    } catch (e) {
      setComments(prev => ({ ...prev, [itemId]: [] }))
    }
  }

  const handleSendComment = async (itemId: string) => {
    if (!project?.id || !commentInputs[itemId]?.trim()) return
    setIsSending(prev => ({ ...prev, [itemId]: true }))
    try {
      await requestMaker.addProjectItemCommentUnauthorized(project.id, itemId, { content: commentInputs[itemId], projectUuid: shareId! })
      setCommentInputs(prev => ({ ...prev, [itemId]: '' }))
      await fetchComments(itemId)
    } finally {
      setIsSending(prev => ({ ...prev, [itemId]: false }))
    }
  }

  useEffect(() => {
    if ((project?.items) == null) return
    project.items.forEach(item => {
      if (item?.id) fetchComments(item.id)
    })
  }, [project?.id, project?.items])

  if (isLoading) {
    return (
      <div className='loading-container'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
        <p className='mt-3'>Ładowanie projektu...</p>
      </div>
    )
  }

  if (project == null) {
    return (
      <div className='tw-container tw-mx-auto tw-px-4 tw-py-12 tw-text-center'>
        <h2 className='tw-text-2xl tw-font-semibold tw-mb-4'>Projekt nie został znaleziony</h2>
        <p className='tw-text-gray-600 tw-mb-6'>Link udostępniający może być nieprawidłowy lub projekt nie jest już dostępny.</p>
        <button
          className={`${primaryButtonClasses}`}
          onClick={() => navigate('/')}
        >
          Powrót do strony głównej
        </button>
      </div>
    )
  }

  const designer = project.designer

  return (
    <>
      {/* <Header /> */}
      <section className='tw-bg-gray-100 tw-pt-10 tw-pb-12 md:tw-pt-12 md:tw-mt-[60px]'>
        <div className='tw-container tw-mx-auto tw-px-4'>
          <div className='tw-mb-8'>
            <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between'>
              <div className='tw-flex tw-items-center tw-mb-4 sm:tw-mb-0'>
                {designer?.imagePresignedUrl && (
                  <div className='tw-mr-3 tw-flex-shrink-0'>
                      <img
                        src={designer.imagePresignedUrl}
                        alt={`${designer.name || 'Projektant'}`}
                        className='tw-h-20 tw-w-20 tw-object-cover tw-rounded-lg'
                      />
                    </div>
                )}
                <div>
                  <h3 className='tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-0'>{designer?.name}</h3>
                  <div className='tw-text-sm tw-text-gray-500 tw-mt-1'>
                      {designer?.phoneNumber && (
                        <div><i className='fa fa-phone tw-mr-1' />{designer.phoneNumber}</div>
                      )}
                      {designer?.email && (
                        <div><i className='fa fa-envelope tw-mr-1' />{designer.email}</div>
                      )}
                      {designer?.street && (
                        <div><i className='fa fa-map-marker tw-mr-1' />{designer.street}</div>
                      )}
                    </div>
                </div>
              </div>
              <div className='tw-flex tw-flex-row tw-items-end tw-ml-8 tw-gap-4'>
                <div className='tw-p-4 tw-mb-2'>
                  <div className='tw-font-semibold tw-text-xl tw-text-gray-800'>{project?.name}</div>
                  <div className='tw-flex tw-items-center tw-text-xs tw-text-gray-500 tw-mt-2'>
                      <span>Suma:&nbsp;</span>
                      <span className='tw-text-sm tw-text-gray-700 tw-font-semibold'>{project?.budgetUsage?.totalCost?.toFixed(2)} zł</span>
                    </div>
                  {project?.budgetUsage?.budget && (
                      <div className='tw-flex tw-items-center tw-text-xs tw-text-gray-500 tw-mt-1'>
                        <span>Budżet:&nbsp;</span>
                        <span className='tw-text-sm tw-text-gray-700 tw-font-semibold'>{project.budgetUsage?.budget?.toFixed(2)} zł</span>
                      </div>
                    )}
                </div>
              </div>
              <div className='tw-flex tw-flex-row tw-items-end tw-ml-8 tw-gap-4'>
                {username && (
                  <button
                      className={`${primaryButtonClasses} tw-flex tw-items-center`}
                      onClick={() => handleSendMessage()}
                    >
                      <i className='fa fa-envelope tw-mr-2' />
                      Wyślij wiadomość
                    </button>
                )}
              </div>
            </div>
          </div>

          <div className='tw-bg-white tw-p-4 md:tw-p-6 tw-rounded-xl tw-shadow-lg tw-mb-8'>
            <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-between sm:tw-items-center tw-mb-5'>
              <h4 className='tw-text-xl tw-font-semibold tw-text-gray-800 tw-mb-3 sm:tw-mb-0'>Elementy projektu</h4>
              <div>
                <button
                  className={`tw-text-xs tw-font-medium tw-py-1.5 tw-px-3 tw-rounded-md tw-mr-2 ${viewMode === 'table' ? 'tw-bg-green-600 tw-text-white hover:tw-bg-green-700 focus:tw-bg-green-700' : 'tw-bg-transparent hover:tw-bg-green-50 tw-text-green-600 tw-border tw-border-solid tw-border-green-500'}`}
                  onClick={() => setViewMode('table')}
                >
                    Widok tabeli
                </button>
                <button
                  className={`tw-text-xs tw-font-medium tw-py-1.5 tw-px-3 tw-rounded-md ${viewMode === 'client' ? 'tw-bg-green-600 tw-text-white hover:tw-bg-green-700 focus:tw-bg-green-700' : 'tw-bg-transparent hover:tw-bg-green-50 tw-text-green-600 tw-border tw-border-solid tw-border-green-500'}`}
                  onClick={() => setViewMode('client')}
                >
                    Widok listy
                </button>
              </div>
            </div>
            {viewMode === 'table'
              ? (
                <ItemsList
                  project={project}
                  openEditModal={async () => {}}
                  handleDeleteItem={async () => {}}
                  handleApproveItem={handleApproveItem}
                  handleRejectItem={handleRejectItem}
                  handleCopyItem={async () => {}}
                  handleMoveItemUp={async () => {}}
                  handleMoveItemDown={async () => {}}
                  readOnly
                  selectedColumns={{ ...selectedColumns, visualization: true, attachment: true }}
                  showAllColumns={false}
                  stageAttachments={stageAttachments}
                />
                )
              : (
                <div className='tw-space-y-10'>
                  {project.stages.map(stage => (
                      <div key={stage.id}>
                        <h5 className='tw-text-lg tw-font-semibold tw-text-gray-700 tw-mb-4 tw-flex tw-items-center'>
                            {stage.name}
                            {stageAttachments[stage.id] && (
                                <a
                                    href={stageAttachments[stage.id].presignedUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='tw-ml-3 tw-text-blue-500 hover:tw-text-blue-600 hover:tw-underline tw-flex tw-items-center tw-text-sm'
                                    title={stageAttachments[stage.id].name}
                                  >
                                    <i className='fa fa-paperclip tw-mr-1' />
                                    <span className='tw-truncate' style={{ maxWidth: '150px' }}>{stageAttachments[stage.id].name}</span>
                                  </a>
                              )}
                          </h5>
                        <div className='tw-space-y-8'>
                            {project.items.filter(item => item.stageId === stage.id).map((item, idx, arr) => (
                                <React.Fragment key={item.id}>
                                    <div className='tw-flex tw-flex-col md:tw-flex-row tw-gap-6 tw-p-4 tw-bg-white tw-rounded-lg tw-shadow tw-border tw-border-gray-200' style={{ minHeight: '220px' }}>
                                        <div className='tw-w-full md:tw-w-1/2 tw-flex tw-flex-col tw-gap-4'>
                  <div className='tw-w-full tw-flex tw-flex-col md:tw-flex-row tw-gap-4'>
                                            <div className='tw-w-full md:tw-w-48 tw-h-48 md:tw-h-auto tw-flex-shrink-0 tw-bg-gray-100 tw-rounded-md tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-2'>
                                                {['product', 'visualization', 'service'].includes(item.type) && <>{item.imageUrl
                                                    ? (
                                                        <img src={item.imageUrl} alt={item.name} className='tw-w-full tw-h-full tw-object-contain' />
                                                      )
                                                    : (
                                                        <i className='fa fa-image tw-text-5xl tw-text-gray-300' />
                                                      )}
                                                                                                                  </>}
                                                <div className='tw-flex tw-mt-3 tw-space-x-3 tw-items-center'>
                                                    <button
                                                        className={`tw-bg-transparent tw-border-none tw-p-0 tw-text-3xl tw-cursor-pointer 
                                                                                    ${item.status === 'approved' ? 'tw-text-green-700' : 'tw-text-green-500 hover:tw-text-green-600'}`}
                                                        onClick={async () => await handleApproveItem(item.id)}
                                                        title={item.status === 'approved' ? 'Cofnij akceptację' : 'Akceptuj'}
                                                      >
                                                        <i className='fa fa-check-circle' />
                                                      </button>
                                                    <button
                                                        className={`tw-bg-transparent tw-border-none tw-p-0 tw-text-3xl tw-cursor-pointer 
                                                                                    ${item.status === 'rejected' ? 'tw-text-red-700' : 'tw-text-red-500 hover:tw-text-red-600'}`}
                                                        onClick={async () => await handleRejectItem(item.id)}
                                                        title={item.status === 'rejected' ? 'Cofnij odrzucenie' : 'Odrzuć'}
                                                      >
                                                        <i className='fa fa-times-circle' />
                                                      </button>

                                                    {item.status === 'approved' && (
                                                        <span className='tw-text-green-600 tw-font-bold tw-text-xs tw-ml-2'>ZAAKCEPTOWANY</span>
                                                      )}
                                                    {item.status === 'rejected' && (
                                                        <span className='tw-text-red-600 tw-font-bold tw-text-xs tw-ml-2'>ODRZUCONY</span>
                                                      )}
                                                    {item.status === NEUTRAL_STATUS && (
                                                        <span className='tw-text-gray-500 tw-font-bold tw-text-xs tw-ml-2'>OCZEKUJĄCY</span>
                                                      )}
                                                  </div>
                                              </div>
                                            <div className='tw-flex-1 tw-pt-2 md:tw-pt-0'>
                                                {['product', 'service'].includes(item.type) && <>
                                                    <h6 className='tw-text-lg tw-font-semibold tw-text-gray-800 tw-flex tw-items-center'>
                                                        {item.name}
                                                        {item.attachmentUrl && (
                                                            <a href={item.attachmentUrl} target='_blank' rel='noopener noreferrer' className='tw-ml-2 tw-text-blue-500 hover:tw-text-blue-600' title='Pobierz załącznik'>
                                                                <i className='fa fa-paperclip' />
                                                              </a>
                                                          )}
                                                      </h6>
                                                    <p className='tw-text-sm tw-text-gray-700 tw-mb-1'>
                                                        <span className='tw-font-semibold'>{item.quantity}</span> {item.unit}, <span className='tw-font-semibold'>{item.price?.toFixed(2)} zł</span> /szt.
                                                      </p>
                                                    <p className='tw-text-sm tw-text-gray-700 tw-mb-2'>
                                                        Wartość: <span className='tw-font-semibold'>{(item.price * item.quantity)?.toFixed(2)} zł</span>
                                                      </p>
                                                    {item.url && <p className='tw-text-xs tw-text-gray-600 tw-mb-1'>Link: <a href={item.url} target='_blank' rel='noopener noreferrer' className='tw-text-blue-500 hover:tw-underline tw-break-all'>{item.url.length > 60 ? item.url.slice(0, 60) + '…' : item.url}</a></p>}
                                                    {item.supplier && <p className='tw-text-xs tw-text-gray-600 tw-mb-1'>Dostawca: <span className='tw-font-semibold'>{item.supplier}</span></p>}
                                                    {item.catalogNumber && <p className='tw-text-xs tw-text-gray-600 tw-mb-1'>Nr kat.: <span className='tw-font-semibold'>{item.catalogNumber}</span></p>}
                                                    {item.dimensions && <p className='tw-text-xs tw-text-gray-600 tw-mb-1'>Wymiary: <span className='tw-font-semibold'>{item.dimensions}</span></p>}
                                                    {item.category && <p className='tw-text-xs tw-text-gray-600 tw-mb-1'>Kategoria: <span className='tw-font-semibold'>{PROJECT_CATEGORIES.find(c => c.id === item.category)?.label}</span></p>}
                                                                                                 </>}
                                                {['product', 'note', 'service'].includes(item.type) && <p className='tw-text-xs tw-text-gray-600 tw-mb-1'>Notatka: <span className='tw-font-semibold'>{item.note}</span></p>}
                                              </div>
                                          </div>

                  {item.visualizationUrl && (
                                            <div className='tw-mt-4'>
                                                <img
                                                    src={item.visualizationUrl}
                                                    alt='Wizualizacja'
                                                    className='tw-w-full tw-max-h-60 tw-object-contain tw-rounded-lg tw-shadow-md tw-border tw-border-gray-200 tw-cursor-pointer'
                                                    onClick={() => {
                                                        setSelectedVisualization(item.visualizationUrl!)
                                                        setShowVisualizationModal(true)
                                                      }}
                                                  />
                                              </div>
                                          )}
                </div>
                                        <div className='tw-w-full md:tw-w-1/2'>
                  <div className='tw-bg-gray-50 md:tw-p-4 tw-rounded-md tw-h-full tw-flex tw-flex-col'>
                                            <h6 className='tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2'>KOMENTARZE</h6>
                                            <div className='tw-flex-grow tw-space-y-2 tw-mb-3 tw-max-h-32 tw-overflow-y-auto tw-text-xs'>
                                                {comments[item.id]?.length
                                                    ? (
                                                        comments[item.id].map(c => (
                                                            <div key={c.id}><span className='tw-font-semibold'>{c.authorName ?? 'Klient'}:</span> {c.content}</div>
                                                        ))
                                                      )
                                                    : (
                                                        <p className='tw-text-gray-500'>BRAK KOMENTARZY</p>
                                                      )}
                                              </div>
                                            <div className='tw-mt-auto'>
                                                <input
                                                    type='text'
                                                    className='tw-w-full tw-px-2 tw-py-1.5 tw-text-xs tw-border tw-border-gray-300 tw-rounded-md tw-mb-2 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500'
                                                    placeholder='Wpisz treść wiadomości'
                                                    value={commentInputs[item.id] || ''}
                                                    onChange={e => setCommentInputs(prev => ({ ...prev, [item.id]: e.target.value }))}
                                                    disabled={isSending[item.id]}
                                                  />
                                                <button
                                                    className={`${outlinePrimaryButtonClasses} ${smallButtonClasses} tw-w-full sm:tw-w-auto`}
                                                    onClick={async () => await handleSendComment(item.id)}
                                                    disabled={isSending[item.id] || !commentInputs[item.id]?.trim()}
                                                  >Wyślij
                                                  </button>
                                              </div>
                                          </div>
                </div>
                                      </div>
                                    {idx < arr.length - 1 && <hr key={`${item.id}-divider`} className='tw-border-0 tw-border-t tw-border-gray-300 tw-my-8' />}
                                  </React.Fragment>
                              ))}
                          </div>
                      </div>
                    ))}
                </div>
                )}
          </div>

          <div className='tw-bg-white tw-p-4 md:tw-p-6 tw-rounded-xl tw-shadow-lg'>
            <AttachmentsSection projectId={project.id} isReadOnly isList />
          </div>
        </div>
      </section>
      {/* <Footer /> */}

      {showVisualizationModal && selectedVisualization && (
        <div
          className='tw-fixed tw-inset-0 tw-bg-black/80 tw-flex tw-justify-center tw-items-center tw-z-50 tw-p-4'
          onClick={() => {
            setShowVisualizationModal(false)
            setSelectedVisualization(null)
          }}
        >
          <img
            src={selectedVisualization}
            alt='Powiększona wizualizacja'
            className='tw-max-w-full tw-max-h-full tw-object-contain tw-rounded-lg tw-shadow-2xl'
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}

export default SharedProjectView
